import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PublicReviewsWall from "@/components/PublicReviewsWall";
import VerifiedBadge, { useVerifiedSeoMeta } from "@/components/VerifiedBadge";
import { toast } from "sonner";
import {
  MapPin, Clock, IndianRupee, ShieldCheck, Star, Share2, Copy,
  Stethoscope, DoorOpen, Users, ArrowRight, Loader2, Phone
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_ORIGIN = window.location.origin;

function setMeta(name, content, property = false) {
  if (!content) return;
  const attr = property ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Public shareable doctor profile — SEO-friendly, no login required.
 * URL: /dr/{slug}
 */
export default function PublicDoctorSharePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/public/dr/${slug}`);
        if (!alive) return;
        setData(data);
      } catch (e) {
        if (!alive) return;
        setErr(e.response?.status === 404 ? "Doctor profile not found." : "Failed to load profile.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    // Poll queue snapshot every 15s
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [slug]);

  // SEO meta tags
  useEffect(() => {
    if (!data) return;
    const s = data.share || {};
    document.title = s.title ? `${s.title} · UpchaarQ` : "Doctor Profile · UpchaarQ";
    setMeta("description", s.description || "");
    setMeta("og:title", s.title, true);
    setMeta("og:description", s.description, true);
    setMeta("og:type", "profile", true);
    setMeta("og:url", `${SITE_ORIGIN}/dr/${slug}`, true);
    if (s.image) setMeta("og:image", s.image, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", s.title);
    setMeta("twitter:description", s.description);
    if (s.image) setMeta("twitter:image", s.image);
  }, [data, slug]);

  const shareLink = `${SITE_ORIGIN}/dr/${slug}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  const shareNative = async () => {
    const s = data?.share || {};
    if (navigator.share) {
      try {
        await navigator.share({ title: s.title, text: s.description, url: shareLink });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  useVerifiedSeoMeta({
    verified: !!data?.org?.cmho_verified,
    entityName: `${data?.doctor?.name || ""} · ${data?.org?.name || ""}`.trim(),
    verifiedAt: data?.org?.cmho_verified_at,
    url: shareLink,
  });

  if (loading && !data) {
    return (
      <div className="min-h-screen min-h-screen-safe flex items-center justify-center text-slate-500 gap-2" data-testid="public-doctor-share-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading profile…
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen min-h-screen-safe flex flex-col items-center justify-center px-4 text-center">
        <p className="text-slate-500 mb-4" data-testid="public-doctor-share-error">{err}</p>
        <Button variant="outline" onClick={() => navigate("/")}>Go home</Button>
      </div>
    );
  }
  const d = data.doctor || {};
  const o = data.org || {};
  const q = data.queue || {};
  const r = data.rating || {};
  const availability = data.availability || {};
  const isFull = !!availability.is_full;
  const waitMin = d.avg_wait_time ?? d.avg_diagnosis_time ?? 15;

  return (
    <div className="min-h-screen min-h-screen-safe bg-slate-50" data-testid="public-doctor-share-page">
      {/* Hero */}
      <header className="bg-gradient-to-br from-sky-600 via-sky-500 to-emerald-500 text-white app-header-safe">
        <div className="max-w-5xl mx-auto px-5 py-8 sm:py-12 px-safe">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white/20 ring-4 ring-white/30 overflow-hidden flex items-center justify-center shrink-0 backdrop-blur">
              {d.profile_pic ? (
                <img src={d.profile_pic} alt={d.name} className="w-full h-full object-cover" />
              ) : (
                <Stethoscope className="w-10 h-10 text-white/80" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading text-3xl sm:text-4xl font-bold" data-testid="dr-name">{d.name}</h1>
                <VerifiedBadge
                  verified={!!o.cmho_verified}
                  verifiedAt={o.cmho_verified_at}
                  variant="solid"
                />
                {d.verified && (
                  <Badge className="bg-emerald-400/30 text-white border border-white/30 gap-1" data-testid="dr-nmc-badge">
                    <ShieldCheck className="w-3 h-3" /> NMC Verified
                  </Badge>
                )}
              </div>
              {d.qualification && <p className="text-sm sm:text-base text-white/90 mt-1" data-testid="dr-qual">{d.qualification}</p>}
              <div className="mt-2 flex items-center gap-3 flex-wrap text-sm text-white/95">
                {d.specialization_en && (
                  <span className="inline-flex items-center gap-1.5" data-testid="dr-spec">
                    <Stethoscope className="w-3.5 h-3.5" />
                    {d.specialization_en}
                    {d.specialization_hi && <span className="opacity-80">· {d.specialization_hi}</span>}
                  </span>
                )}
                {r.count > 0 && (
                  <span className="inline-flex items-center gap-1 bg-amber-400/25 px-2 py-0.5 rounded-full" data-testid="dr-rating">
                    <Star className="w-3.5 h-3.5 fill-amber-300 text-amber-300" />
                    <b>{r.avg?.toFixed(1)}</b>
                    <span className="opacity-80">({r.count} review{r.count !== 1 ? "s" : ""})</span>
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <Button
                data-testid="share-copy-btn"
                onClick={shareNative}
                className="bg-white/95 text-sky-700 hover:bg-white gap-1.5">
                <Share2 className="w-4 h-4" /> Share
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-8 grid md:grid-cols-3 gap-5">
        <div className="md:col-span-2 space-y-5">
          {/* Book CTA */}
          <Card className={`medical-card p-5 ${isFull ? "border-rose-200 bg-rose-50/70" : "border-emerald-200 bg-emerald-50/70"}`} data-testid="book-cta-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className={`text-xs uppercase tracking-[0.18em] font-semibold ${isFull ? "text-rose-700" : "text-emerald-700"}`}>
                  {isFull ? "Fully Booked Today" : "Live Queue"}
                </p>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-2xl font-heading font-bold text-slate-900" data-testid="queue-current">
                    {q.current ? `Token #${q.current}` : "Available"}
                  </span>
                  {(q.waiting_count ?? 0) > 0 && (
                    <span className="text-xs text-slate-500">· {q.waiting_count} waiting</span>
                  )}
                </div>
                {isFull ? (
                  <p className="text-xs text-rose-700 mt-1" data-testid="book-full-notice">
                    Today's seats are full{availability.limit ? ` (${availability.limit}/${availability.limit})` : ""}. Please pick the next available date or check back tomorrow.
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">
                    Estimated wait ~{waitMin} min per patient
                    {availability.limit && ` · ${availability.remaining} of ${availability.limit} seats left today`}
                  </p>
                )}
              </div>
              <Button
                data-testid="book-now-btn"
                onClick={() => navigate(`/patient/doctor/${d.id}`)}
                disabled={isFull}
                className={isFull
                  ? "bg-slate-300 text-slate-500 cursor-not-allowed gap-1.5 h-11 px-6 tap-target"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 h-11 px-6 tap-target"}>
                {isFull
                  ? (<><span aria-hidden>🚫</span> Today's Bookings Full / आज की सीटें फुल हैं</>)
                  : (<>Book Appointment <ArrowRight className="w-4 h-4" /></>)}
              </Button>
            </div>
          </Card>

          {/* Details */}
          <Card className="medical-card p-5" data-testid="details-card">
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Consultation Details</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <InfoRow icon={IndianRupee} label="Consultation Fee" value={d.consultation_fee ? `₹${d.consultation_fee}` : "—"} />
              <InfoRow icon={Clock} label="Avg Wait Time" value={`~${waitMin} min`} />
              <InfoRow icon={DoorOpen} label="Room Number" value={d.room_number || "—"} />
              <InfoRow icon={Users} label="Trust Score" value={`${d.trust_score || 0} referrals`} />
            </div>
          </Card>

          {/* Hospital */}
          {o.name && (
            <Card className="medical-card p-5" data-testid="hospital-card">
              <div className="flex items-start gap-4">
                {o.image && (
                  <img src={o.image} alt={o.name} className="w-16 h-16 rounded-lg object-cover" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{o.type || "Hospital"}</p>
                  <h3 className="font-heading text-lg font-semibold text-slate-900">{o.name}</h3>
                  {o.address && (
                    <p className="text-sm text-slate-500 mt-1 flex items-start gap-1.5">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{o.address}{o.city ? `, ${o.city}` : ""}</span>
                    </p>
                  )}
                  {o.phone && (
                    <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" /> {o.phone}
                    </p>
                  )}
                  {o.working_hours && (
                    <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> {o.working_hours}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          )}

          <PublicReviewsWall doctorId={d.id} />
        </div>

        {/* Side share panel */}
        <aside className="space-y-5">
          <Card className="medical-card p-5" data-testid="share-panel">
            <h3 className="font-heading text-base font-semibold text-slate-900">Share this profile</h3>
            <p className="text-xs text-slate-500 mt-1">Send patients directly to the booking page.</p>
            <div className="mt-3 flex items-center gap-2 bg-slate-50 rounded-lg p-2.5 text-xs text-slate-700 truncate" data-testid="share-link-preview">
              {shareLink}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" onClick={copyLink} data-testid="share-copy-inline" className="gap-1.5">
                <Copy className="w-3.5 h-3.5" /> Copy
              </Button>
              <Button size="sm"
                data-testid="share-whatsapp-btn"
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`${data.share?.title || d.name}\n${shareLink}`)}`, "_blank")}
                className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5">
                Share on WhatsApp
              </Button>
            </div>
          </Card>
        </aside>
      </main>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-sky-600" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</p>
        <p className="text-sm font-medium text-slate-900 mt-0.5">{value}</p>
      </div>
    </div>
  );
}
